import React, { forwardRef } from 'react'
import { Text, Flex } from '@chakra-ui/core'
import DatePicker, { ReactDatePickerProps } from 'react-datepicker'
import { CustomTooltip } from '../helperStyles'
import { CalendarDays } from 'lucide-react'
import LocaleText from 'components/LocaleText'
import dayjs from 'dayjs'
import { useSelector } from 'react-redux'
import { useDatePickerLocale } from 'hooks/useDatePickerLocale'
import { useDateLocale } from 'hooks/useDateLocale'
import { RootState } from 'constants/interfaces'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import useLocaleText from 'components/useLocaleText'

dayjs.extend(localizedFormat)

interface IProps extends ReactDatePickerProps {
  tooltipLabel?: string
  isShowYearPicker?: boolean
  isShowMonthPicker?: boolean
}
interface ICustomTrigger {
  onClick?: VoidFunction
  value?: string | Date
  label?: string
  placeholderText?: string
}

const CustomTrigger = forwardRef<HTMLElement, ICustomTrigger>(({ label, value, placeholderText, onClick }, ref) => {
  const locale = useSelector((state: RootState) => state.general.locale)
  const t_select_date = useLocaleText(placeholderText || 'select_date', true)

  let displayValue = t_select_date || ''

  if (value) {
    let parsed
    if (value instanceof Date) {
      parsed = dayjs(value)
    } else if (typeof value === 'string') {
      parsed = dayjs(value)
      if (!parsed.isValid()) {
        displayValue = value
      }
    } else {
      parsed = dayjs(value)
    }

    if (parsed && parsed.isValid()) {
      displayValue = parsed.format('DD/MM/YYYY, HH:mm')
    }
  }

  return (
    <CustomTooltip
      display={label ? 'block' : 'none'}
      aria-label={label || ''}
      label={label || ''}
      placement="bottom-start"
    >
      <Flex
        onClick={onClick}
        ref={ref}
        alignItems="center"
        cursor="pointer"
        w="100%"
        p={2}
        bg="white"
        border="1px solid"
        borderColor="gray.300"
        borderRadius="5px"
        style={{ gap: '10px' }}
      >
        <CalendarDays size={14} />
        <Text fontSize="14px" fontWeight={500}>
          <LocaleText text={displayValue} />
        </Text>
      </Flex>
    </CustomTooltip>
  )
})

const CustomDatePicker: React.FC<IProps> = ({
  tooltipLabel,
  placeholderText,
  isShowMonthPicker = false,
  isShowYearPicker = false,
  ...props
}: IProps) => {
  const locale = useSelector((state: RootState) => state.general.locale)
  const datePickerLocale = useDatePickerLocale(locale)

  return (
    <DatePicker
      showTimeInput
      timeInputLabel="Time:"
      timeIntervals={5}
      selected={props.value ? new Date(props.value) : null}
      shouldCloseOnSelect={false}
      dateFormat={props.dateFormat || 'MM/dd/yyyy h:mm aa'}
      className="datePicker"
      popperPlacement="auto"
      locale={datePickerLocale}
      timeFormat="HH:mm"
      showMonthDropdown={isShowMonthPicker}
      showYearDropdown={isShowYearPicker}
      customInput={React.cloneElement(<CustomTrigger placeholderText={placeholderText} label={tooltipLabel} />, {
        value: props.value,
      })}
      {...props}
    />
  )
}

export default CustomDatePicker
